import { QueryClient } from '@tanstack/react-query';
import { debugError, debugLog, debugWarn } from '../utils/debugLog';
import { performanceServiceManager } from './performanceServiceManager';
import { getImageCacheMetrics } from './imageCacheService';

type PerformanceMemoryData = {
  usedJSHeapSize?: number;
};

const getBrowserMemoryUsage = (): number => {
  if (typeof performance === 'undefined') {
    return 0;
  }

  const perf = performance as Performance & { memory?: PerformanceMemoryData };
  const usedHeap = perf.memory?.usedJSHeapSize;

  return typeof usedHeap === 'number' ? usedHeap : 0;
};

// Aggregated metrics interfaces
export interface AggregatedMetrics {
  timestamp: Date;
  
  // Cache Performance
  cache: {
    hitRate: number;
    missRate: number;
    totalRequests: number;
    memoryUsage: number;
    apiCallFrequency: number;
  };
  
  // React Query Metrics
  reactQuery: {
    totalQueries: number;
    activeQueries: number;
    staleQueries: number;
    errorQueries: number;
    fetchingQueries: number;
    cacheSize: number;
  };
  
  // Browser Performance
  browser: {
    memoryUsage: number;
    navigationTiming: {
      domContentLoaded: number;
      loadComplete: number;
      firstPaint: number;
      firstContentfulPaint: number;
    };
    resourceTiming: {
      totalResources: number;
      averageLoadTime: number;
      slowestResource: string;
      slowestResourceTime: number;
    };
  };
  
  // Custom Application Metrics
  application: {
    componentRenderCount: number;
    errorBoundaryTriggers: number;
    userInteractions: number;
    routeChanges: number;
  };

  // Image performance metrics
  imagePerformance: {
    totalLoads: number;
    hitRate: number;
    missRate: number;
    averageLoadTime: number;
    mobileShare: number;
    desktopShare: number;
    lastUpdated: number;
  };
  
  // Performance Score
  overallScore: number;
  categoryScores: {
    caching: number;
    memory: number;
    network: number;
    rendering: number;
  };
}

export interface PerformanceAlert {
  id: string;
  timestamp: Date;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'cache' | 'memory' | 'network' | 'rendering' | 'error';
  metric: string;
  currentValue: number;
  threshold: number;
  message: string;
  resolved: boolean;
  resolvedAt?: Date;
}

export interface PerformanceThreshold {
  metric: string;
  category: 'cache' | 'memory' | 'network' | 'rendering' | 'error';
  warning: number;
  critical: number;
  comparison: 'greater' | 'less';
  enabled: boolean;
}

export interface MetricsExportData {
  exportId: string;
  timestamp: Date;
  timeRange: {
    start: Date;
    end: Date;
  };
  metrics: AggregatedMetrics[];
  summary: {
    averageScore: number;
    bestScore: number;
    worstScore: number;
    totalAlerts: number;
    criticalAlerts: number;
    trends: {
      cacheHitRate: 'improving' | 'stable' | 'declining';
      memoryUsage: 'improving' | 'stable' | 'declining';
      overallPerformance: 'improving' | 'stable' | 'declining';
    };
  };
}

/**
 * MetricsAggregationService - Centralized Performance Data Collection
 * 
 * Features:
 * - Aggregates data from multiple performance sources
 * - Historical data storage with configurable retention
 * - Performance threshold monitoring with alerts
 * - Data export capabilities for analysis
 * - Real-time performance scoring
 * - Trend analysis and reporting
 */
export class MetricsAggregationService {
  private queryClient: QueryClient;
  private metricsHistory: AggregatedMetrics[] = [];
  private alerts: PerformanceAlert[] = [];
  private thresholds: PerformanceThreshold[] = [];
  private listeners: Set<(metrics: AggregatedMetrics) => void> = new Set();
  private alertListeners: Set<(alert: PerformanceAlert) => void> = new Set();
  
  // Configuration
  private maxHistorySize: number = 1000; // Keep last 1000 data points
  private collectionInterval: number = 5000; // Collect every 5 seconds
  private intervalId: NodeJS.Timeout | null = null;
  
  // Performance tracking
  private startTime: number = Date.now();
  private customMetrics: Map<string, number> = new Map();

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.initializeDefaultThresholds();
    this.startCollection();
    
    debugLog('📊 MetricsAggregationService initialized');
  }

  /**
   * Start automatic metrics collection
   */
  private startCollection(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    
    this.intervalId = setInterval(() => {
      this.collectMetrics();
    }, this.collectionInterval);
  }

  /**
   * Collect metrics from all sources
   */
  private async collectMetrics(): Promise<void> {
    try {
      const timestamp = new Date();
      
      // Collect cache performance metrics
      const cacheMetrics = performanceServiceManager.getBasicMetrics();
      
      // Collect React Query metrics
      const queryCache = this.queryClient.getQueryCache();
      const queries = queryCache.getAll();
      const cacheSize = this.estimateCacheSize(queries);
      
      // Collect browser performance metrics
      const browserMetrics = this.collectBrowserMetrics();
      
      // Collect application metrics
      const applicationMetrics = this.collectApplicationMetrics();
      const imagePerformanceMetrics = this.collectImagePerformanceMetrics();
      
      // Calculate performance scores
      const categoryScores = this.calculateCategoryScores(
        cacheMetrics,
        queries,
        browserMetrics,
        applicationMetrics
      );
      
      const overallScore = this.calculateOverallScore(categoryScores);
      
      const aggregatedMetrics: AggregatedMetrics = {
        timestamp,
        cache: {
          hitRate: cacheMetrics.hitRate,
          missRate: cacheMetrics.missRate,
          totalRequests: cacheMetrics.totalRequests,
          memoryUsage: cacheMetrics.memoryUsage,
          apiCallFrequency: cacheMetrics.apiCalls.length,
        },
        reactQuery: {
          totalQueries: queries.length,
          activeQueries: queries.filter(q => q.state.fetchStatus === 'fetching').length,
          staleQueries: queries.filter(q => q.isStale()).length,
          errorQueries: queries.filter(q => q.state.status === 'error').length,
          fetchingQueries: queries.filter(q => q.state.fetchStatus === 'fetching').length,
          cacheSize,
        },
        browser: browserMetrics,
        application: applicationMetrics,
        imagePerformance: imagePerformanceMetrics,
        overallScore,
        categoryScores,
      };
      
      // Store metrics
      this.addMetrics(aggregatedMetrics);
      
      // Check thresholds and generate alerts
      this.checkThresholds(aggregatedMetrics);
      
      // Notify listeners
      this.notifyListeners(aggregatedMetrics);
      
    } catch (error) {
      debugError('Error collecting metrics:', error);
    }
  }

  /**
   * Collect browser performance metrics
   */
  private collectBrowserMetrics() {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType('paint');
    const resources = performance.getEntriesByType('resource');
    
    // Calculate resource timing stats
    const resourceTimes = resources.map(r => r.duration);
    const averageLoadTime = resourceTimes.length > 0 
      ? resourceTimes.reduce((sum, time) => sum + time, 0) / resourceTimes.length 
      : 0;
    
    const slowestResource = resources.reduce((slowest, current) => 
      current.duration > slowest.duration ? current : slowest, 
      resources[0] || { name: 'none', duration: 0 }
    );

    if (typeof performance.clearResourceTimings === 'function' && resources.length > 200) {
      performance.clearResourceTimings();
    }

    return {
      memoryUsage: getBrowserMemoryUsage(),
      navigationTiming: {
        domContentLoaded: navigation ? navigation.domContentLoadedEventEnd - navigation.fetchStart : 0,
        loadComplete: navigation ? navigation.loadEventEnd - navigation.fetchStart : 0,
        firstPaint: paint.find(p => p.name === 'first-paint')?.startTime || 0,
        firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime || 0,
      },
      resourceTiming: {
        totalResources: resources.length,
        averageLoadTime,
        slowestResource: slowestResource?.name || 'none',
        slowestResourceTime: slowestResource?.duration || 0,
      },
    };
  }

  /**
   * Collect application-specific metrics
   */
  private collectApplicationMetrics() {
    return {
      componentRenderCount: this.customMetrics.get('componentRenders') || 0,
      errorBoundaryTriggers: this.customMetrics.get('errorBoundaryTriggers') || 0,
      userInteractions: this.customMetrics.get('userInteractions') || 0,
      routeChanges: this.customMetrics.get('routeChanges') || 0,
    };
  }

  /**
   * Collect image performance metrics from the image cache service
   */
  private collectImagePerformanceMetrics() {
    try {
      const snapshot = getImageCacheMetrics();
      const loads = snapshot.loads;
      const total = loads.total || 0;

      const toPercentage = (value: number) => {
        if (!total || !Number.isFinite(value)) {
          return 0;
        }
        return Math.max(0, Math.min(100, (value / total) * 100));
      };

      const averageLoadTime = Number.isFinite(loads.averageLoadTime) ? loads.averageLoadTime : 0;

      return {
        totalLoads: total,
        hitRate: toPercentage(loads.hits),
        missRate: toPercentage(loads.misses),
        averageLoadTime,
        mobileShare: toPercentage(loads.mobileLoads),
        desktopShare: toPercentage(loads.desktopLoads),
        lastUpdated: snapshot.lastUpdated,
      };
    } catch (error) {
      debugWarn('⚠️ Error collecting image performance metrics:', error);
      return {
        totalLoads: 0,
        hitRate: 0,
        missRate: 0,
        averageLoadTime: 0,
        mobileShare: 0,
        desktopShare: 0,
        lastUpdated: Date.now(),
      };
    }
  }

  /**
   * Estimate React Query cache size
   */
  private estimateCacheSize(queries: any[]): number {
    return queries.reduce((size, query) => {
      const data = query.state.data;
      if (data) {
        // Rough estimation of data size
        return size + JSON.stringify(data).length;
      }
      return size;
    }, 0);
  }

  /**
   * Calculate category performance scores
   */
  private calculateCategoryScores(
    cacheMetrics: any,
    queries: any[],
    browserMetrics: any,
    applicationMetrics: any
  ) {
    // Caching score (0-100)
    const cachingScore = Math.min(100, Math.max(0, 
      (cacheMetrics.hitRate * 0.7) + 
      (Math.max(0, 100 - (cacheMetrics.apiCallFrequency || 0) * 2) * 0.3)
    ));
    
    // Memory score (0-100)
    const memoryUsageMB = browserMetrics.memoryUsage / (1024 * 1024);
    const memoryScore = Math.min(100, Math.max(0, 100 - (memoryUsageMB - 50) * 2)); // Penalty after 50MB
    
    // Network score (0-100)
    const networkScore = Math.min(100, Math.max(0, 
      100 - ((browserMetrics.resourceTiming?.averageLoadTime || 500) / 10)
    ));
    
    // Rendering score (0-100)
    const errorRate = queries.length > 0 ? (queries.filter(q => q.state?.status === 'error').length / queries.length) * 100 : 0;
    const renderingScore = Math.min(100, Math.max(0, 100 - errorRate * 10));
    
    return {
      caching: Math.round(cachingScore),
      memory: Math.round(memoryScore),
      network: Math.round(networkScore),
      rendering: Math.round(renderingScore),
    };
  }

  /**
   * Calculate overall performance score
   */
  private calculateOverallScore(categoryScores: any): number {
    const weights = {
      caching: 0.3,
      memory: 0.25,
      network: 0.25,
      rendering: 0.2,
    };
    
    return Math.round(
      categoryScores.caching * weights.caching +
      categoryScores.memory * weights.memory +
      categoryScores.network * weights.network +
      categoryScores.rendering * weights.rendering
    );
  }

  /**
   * Add metrics to history
   */
  private addMetrics(metrics: AggregatedMetrics): void {
    this.metricsHistory.push(metrics);
    
    // Trim history if it exceeds max size
    if (this.metricsHistory.length > this.maxHistorySize) {
      this.metricsHistory = this.metricsHistory.slice(-this.maxHistorySize);
    }
  }

  /**
   * Check performance thresholds and generate alerts
   */
  private checkThresholds(metrics: AggregatedMetrics): void {
    this.thresholds.forEach(threshold => {
      if (!threshold.enabled) return;
      
      const currentValue = this.getMetricValue(metrics, threshold.metric);
      if (currentValue === null) return;
      
      const exceedsWarning = threshold.comparison === 'greater' 
        ? currentValue > threshold.warning 
        : currentValue < threshold.warning;
        
      const exceedsCritical = threshold.comparison === 'greater' 
        ? currentValue > threshold.critical 
        : currentValue < threshold.critical;
      
      if (exceedsCritical || exceedsWarning) {
        const severity = exceedsCritical ? 'critical' : 'high';
        const thresholdValue = exceedsCritical ? threshold.critical : threshold.warning;
        
        const alert: PerformanceAlert = {
          id: `${threshold.metric}-${Date.now()}`,
          timestamp: new Date(),
          severity,
          category: threshold.category,
          metric: threshold.metric,
          currentValue,
          threshold: thresholdValue,
          message: `${threshold.metric} ${threshold.comparison === 'greater' ? 'exceeded' : 'below'} ${severity} threshold: ${currentValue} ${threshold.comparison === 'greater' ? '>' : '<'} ${thresholdValue}`,
          resolved: false,
        };
        
        this.addAlert(alert);
      }
    });
  }

  /**
   * Get metric value by path
   */
  private getMetricValue(metrics: AggregatedMetrics, metricPath: string): number | null {
    const parts = metricPath.split('.');
    let value: any = metrics;
    
    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return null;
      }
    }
    
    return typeof value === 'number' ? value : null;
  }

  /**
   * Add alert
   */
  private addAlert(alert: PerformanceAlert): void {
    // Check if similar alert already exists and is unresolved
    const existingAlert = this.alerts.find(a => 
      a.metric === alert.metric && 
      a.category === alert.category && 
      !a.resolved
    );
    
    if (!existingAlert) {
      this.alerts.push(alert);
      this.notifyAlertListeners(alert);
      debugWarn(`🚨 Performance Alert: ${alert.message}`);
    }
  }

  /**
   * Initialize default performance thresholds
   */
  private initializeDefaultThresholds(): void {
    this.thresholds = [
      {
        metric: 'cache.hitRate',
        category: 'cache',
        warning: 70,
        critical: 50,
        comparison: 'less',
        enabled: true,
      },
      {
        metric: 'browser.memoryUsage',
        category: 'memory',
        warning: 100 * 1024 * 1024, // 100MB
        critical: 200 * 1024 * 1024, // 200MB
        comparison: 'greater',
        enabled: true,
      },
      {
        metric: 'reactQuery.errorQueries',
        category: 'error',
        warning: 3,
        critical: 5,
        comparison: 'greater',
        enabled: true,
      },
      {
        metric: 'overallScore',
        category: 'rendering',
        warning: 70,
        critical: 50,
        comparison: 'less',
        enabled: true,
      },
    ];
  }

  // Public API methods

  /**
   * Get current metrics
   */
  public getCurrentMetrics(): AggregatedMetrics | null {
    return this.metricsHistory[this.metricsHistory.length - 1] || null;
  }

  /**
   * Get metrics history
   */
  public getMetricsHistory(limit?: number): AggregatedMetrics[] {
    if (limit) {
      return this.metricsHistory.slice(-limit);
    }
    return [...this.metricsHistory];
  }

  /**
   * Get active alerts
   */
  public getActiveAlerts(): PerformanceAlert[] {
    return this.alerts.filter(alert => !alert.resolved);
  }

  /**
   * Get all alerts
   */
  public getAllAlerts(): PerformanceAlert[] {
    return [...this.alerts];
  }

  /**
   * Resolve alert
   */
  public resolveAlert(alertId: string): void {
    const alert = this.alerts.find(a => a.id === alertId);
    if (alert) {
      alert.resolved = true;
      alert.resolvedAt = new Date();
    }
  }

  /**
   * Add custom metric
   */
  public addCustomMetric(name: string, value: number): void {
    this.customMetrics.set(name, value);
  }

  /**
   * Increment custom metric
   */
  public incrementCustomMetric(name: string, increment: number = 1): void {
    const current = this.customMetrics.get(name) || 0;
    this.customMetrics.set(name, current + increment);
  }

  /**
   * Export metrics data
   */
  public exportMetrics(startDate?: Date, endDate?: Date): MetricsExportData {
    const now = new Date();
    const start = startDate || new Date(now.getTime() - 24 * 60 * 60 * 1000); // Last 24 hours
    const end = endDate || now;
    
    const filteredMetrics = this.metricsHistory.filter(m => 
      m.timestamp >= start && m.timestamp <= end
    );
    
    const scores = filteredMetrics.map(m => m.overallScore);
    const cacheHitRates = filteredMetrics.map(m => m.cache.hitRate);
    const memoryUsages = filteredMetrics.map(m => m.browser.memoryUsage);
    
    return {
      exportId: `export-${Date.now()}`,
      timestamp: now,
      timeRange: { start, end },
      metrics: filteredMetrics,
      summary: {
        averageScore: scores.length > 0 ? scores.reduce((sum, s) => sum + s, 0) / scores.length : 0,
        bestScore: scores.length > 0 ? Math.max(...scores) : 0,
        worstScore: scores.length > 0 ? Math.min(...scores) : 0,
        totalAlerts: this.alerts.filter(a => a.timestamp >= start && a.timestamp <= end).length,
        criticalAlerts: this.alerts.filter(a => a.timestamp >= start && a.timestamp <= end && a.severity === 'critical').length,
        trends: {
          cacheHitRate: this.calculateTrend(cacheHitRates),
          memoryUsage: this.calculateTrend(memoryUsages, true), // Reverse for memory (lower is better)
          overallPerformance: this.calculateTrend(scores),
        },
      },
    };
  }

  /**
   * Calculate trend from array of values
   */
  private calculateTrend(values: number[], reverse: boolean = false): 'improving' | 'stable' | 'declining' {
    if (values.length < 2) return 'stable';
    
    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));
    
    const firstAvg = firstHalf.reduce((sum, v) => sum + v, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, v) => sum + v, 0) / secondHalf.length;
    
    const change = (secondAvg - firstAvg) / firstAvg;
    const threshold = 0.05; // 5% change threshold
    
    if (reverse) {
      if (change < -threshold) return 'improving';
      if (change > threshold) return 'declining';
    } else {
      if (change > threshold) return 'improving';
      if (change < -threshold) return 'declining';
    }
    
    return 'stable';
  }

  /**
   * Add metrics listener
   */
  public addMetricsListener(listener: (metrics: AggregatedMetrics) => void): void {
    this.listeners.add(listener);
  }

  /**
   * Remove metrics listener
   */
  public removeMetricsListener(listener: (metrics: AggregatedMetrics) => void): void {
    this.listeners.delete(listener);
  }

  /**
   * Add alert listener
   */
  public addAlertListener(listener: (alert: PerformanceAlert) => void): void {
    this.alertListeners.add(listener);
  }

  /**
   * Remove alert listener
   */
  public removeAlertListener(listener: (alert: PerformanceAlert) => void): void {
    this.alertListeners.delete(listener);
  }

  /**
   * Notify metrics listeners
   */
  private notifyListeners(metrics: AggregatedMetrics): void {
    this.listeners.forEach(listener => {
      try {
        listener(metrics);
      } catch (error) {
        debugError('Error in metrics listener:', error);
      }
    });
  }

  /**
   * Notify alert listeners
   */
  private notifyAlertListeners(alert: PerformanceAlert): void {
    this.alertListeners.forEach(listener => {
      try {
        listener(alert);
      } catch (error) {
        debugError('Error in alert listener:', error);
      }
    });
  }

  /**
   * Update collection interval
   */
  public setCollectionInterval(intervalMs: number): void {
    this.collectionInterval = intervalMs;
    this.startCollection(); // Restart with new interval
  }

  /**
   * Update max history size
   */
  public setMaxHistorySize(size: number): void {
    this.maxHistorySize = size;
    
    // Trim current history if needed
    if (this.metricsHistory.length > size) {
      this.metricsHistory = this.metricsHistory.slice(-size);
    }
  }

  /**
   * Add or update performance threshold
   */
  public setThreshold(threshold: PerformanceThreshold): void {
    const existingIndex = this.thresholds.findIndex(t => 
      t.metric === threshold.metric && t.category === threshold.category
    );
    
    if (existingIndex >= 0) {
      this.thresholds[existingIndex] = threshold;
    } else {
      this.thresholds.push(threshold);
    }
  }

  /**
   * Get performance thresholds
   */
  public getThresholds(): PerformanceThreshold[] {
    return [...this.thresholds];
  }

  /**
   * Clear all metrics history
   */
  public clearHistory(): void {
    this.metricsHistory.length = 0;
    debugLog('📊 Metrics history cleared');
  }

  /**
   * Clear all alerts
   */
  public clearAlerts(): void {
    this.alerts.length = 0;
    debugLog('🚨 Alerts cleared');
  }

  /**
   * Destroy the service
   */
  public destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    this.listeners.clear();
    this.alertListeners.clear();
    this.metricsHistory.length = 0;
    this.alerts.length = 0;
    this.customMetrics.clear();
    
    debugLog('📊 MetricsAggregationService destroyed');
  }
}

export default MetricsAggregationService;
